/**
 * Web-push subscription helper — call `enablePush(api)` after user opt-in.
 * Returns { enabled: bool, permission: string, subscription: object|null }.
 */
import { api } from "@/lib/api";

const urlB64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const pushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const pushPermission = () => (pushSupported() ? Notification.permission : "unsupported");

export async function enablePush() {
  if (!pushSupported()) throw new Error("Push not supported in this browser");
  const reg = await navigator.serviceWorker.ready;
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission denied");

  const { data } = await api.get("/push/public-key");
  const applicationServerKey = urlB64ToUint8Array(data.public_key);

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

export async function disablePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await api.post("/push/unsubscribe", sub.toJSON());
    } catch (err) {
      // Server may already have cleaned up the subscription; unsubscribe locally regardless.
      console.warn("push unsubscribe API failed", err);
    }
    await sub.unsubscribe();
  }
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}
